/**
 * Plan 04-02 (DRC-05) — magic-link password set frontend service.
 *
 * The pre-existing `authService` object lives in userService.ts and uses
 * the authenticated default client for /login, /me, /logout, etc. The
 * set-password endpoint is
 * anonymous (no JWT yet), so it MUST go through `apiPublic` — the B2 lock.
 * This file extends the surface with `setPassword` + `requestPasswordSet`
 * via `apiPublic` and re-exports the pre-existing `authService` for callers
 * that want a single import point.
 */
import { apiPublic } from './apiService';
import { authService as legacyAuthService } from './userService';

export interface SetPasswordPayload {
  token: string;
  new_password: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

// Anonymous endpoint — MUST call apiPublic (no auth interceptor). B2 lock.
async function setPassword(payload: SetPasswordPayload): Promise<TokenResponse> {
  const resp = await apiPublic.post<TokenResponse>('/api/v1/auth/set-password', payload);
  return resp.data;
}

// Public surface for symmetry; the authenticated server-side hook in
// POST /adt/agency_members calls the Python helper directly. If a frontend
// caller ever needs this (e.g. "resend invite"), apiPublic keeps semantics
// consistent with the anonymous set-password flow. B2 lock.
async function requestPasswordSet(user_id: number): Promise<{ sent: boolean; message: string }> {
  const resp = await apiPublic.post<{ sent: boolean; message: string }>(
    '/api/v1/auth/request-password-set',
    { user_id },
  );
  return resp.data;
}

// Compose the two new methods with the legacy authService surface so any
// caller (existing or new) can `import { authService } from './authService'`.
export const authService = {
  ...legacyAuthService,
  setPassword,
  requestPasswordSet,
};

export default authService;
